import React, { useEffect, useState } from 'react';

/**
 * Corner ribbon rendered on package cards when a package is on offer.
 * Shows an optional live countdown when offer_ends_at is in the future.
 *
 * Props:
 *  - text: string (ribbon label, e.g. "Limited Offer")
 *  - endsAt: ISO datetime string (optional) — when set + in future, a countdown chip appears
 *  - variant: 'card' | 'detail'  — controls size & position tweaks
 */
const pad = (n) => String(n).padStart(2, '0');

export const formatOfferRemaining = (endsAt, now = Date.now()) => {
  if (!endsAt) return null;
  const endsMs = new Date(endsAt).getTime();
  const ms = endsMs - now;
  if (ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Hook that returns a live-updating "Xd Yh Zm" string for a given offer_ends_at.
 * Returns null when the offer has expired or endsAt isn't provided.
 */
export const useOfferCountdown = (endsAt) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endsAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return formatOfferRemaining(endsAt, now);
};

const OfferRibbon = ({ text = 'Limited Offer', endsAt = null, variant = 'card' }) => {
  // Countdown is now rendered inline under the offer price (see PriceTag).
  // The ribbon itself only shows the diagonal offer badge.
  const isDetail = variant === 'detail';

  return (
    <>
      {/* Diagonal corner ribbon (top-left) — wider so the label is prominent */}
      <div
        className={`absolute top-0 left-0 overflow-hidden pointer-events-none ${isDetail ? 'w-40 h-40' : 'w-32 h-32'}`}
        data-testid="offer-ribbon"
      >
        <div
          className="absolute bg-gradient-to-r from-rose-600 to-red-500 text-white font-bold uppercase tracking-wider text-center shadow-lg"
          style={{
            width: isDetail ? 220 : 180,
            padding: isDetail ? '7px 0' : '6px 0',
            top: isDetail ? 40 : 32,
            left: isDetail ? -56 : -46,
            transform: 'rotate(-45deg)',
            letterSpacing: '0.09em',
            fontSize: isDetail ? 13 : 11,
          }}
        >
          {text || 'Limited Offer'}
        </div>
      </div>
    </>
  );
};

export default OfferRibbon;
